import { useState, useEffect, useRef, useMemo } from 'react'
import { Check, X, AlertCircle, Clipboard } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface RecoveryPhraseInputProps {
  expectedMnemonic?: string
  onValidationChange?: (isValid: boolean, userMnemonic: string) => void
  showValidation?: boolean
  className?: string
  title?: string
}

const RecoveryPhraseInput = ({
  expectedMnemonic,
  onValidationChange,
  showValidation = true,
  className = '',
  title
}: RecoveryPhraseInputProps) => {
  const { t } = useTranslation()
  const [words, setWords] = useState<string[]>(Array(12).fill(''))
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const [validationState, setValidationState] = useState<{
    isValid: boolean
    incorrectIndices: number[]
  }>({ isValid: false, incorrectIndices: [] })
  const [pasted, setPasted] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Memoize expectedWords to prevent recreating the array on every render
  const expectedWords = useMemo(
    () =>
      expectedMnemonic
        ? expectedMnemonic.trim().toLowerCase().split(/\s+/)
        : [],
    [expectedMnemonic]
  )

  // Validate words whenever they change
  useEffect(() => {
    const userWords = words.map((w) => w.trim().toLowerCase())
    const allFilled = userWords.every((w) => w.length > 0)

    if (allFilled) {
      // If no expected mnemonic, just check if all 12 words are filled
      if (!expectedMnemonic) {
        const isValid =
          userWords.length === 12 && userWords.every((w) => w.length > 0)
        setValidationState({ isValid, incorrectIndices: [] })
        if (onValidationChange) {
          onValidationChange(isValid, userWords.join(' '))
        }
      } else {
        // Validate against expected mnemonic
        const incorrectIndices: number[] = []
        userWords.forEach((word, index) => {
          if (word !== expectedWords[index]) {
            incorrectIndices.push(index)
          }
        })

        const isValid = incorrectIndices.length === 0
        setValidationState({ isValid, incorrectIndices })

        if (onValidationChange) {
          onValidationChange(isValid, userWords.join(' '))
        }
      }
    } else {
      setValidationState({ isValid: false, incorrectIndices: [] })
      if (onValidationChange) {
        onValidationChange(false, userWords.join(' '))
      }
    }
  }, [words, expectedWords, expectedMnemonic, onValidationChange])

  const handleWordChange = (index: number, value: string) => {
    // Only allow letters and spaces, convert to lowercase
    const cleanValue = value.toLowerCase().replace(/[^a-z\s]/g, '')

    const newWords = [...words]
    newWords[index] = cleanValue

    setWords(newWords)

    // Auto-advance to next field if space is pressed or word looks complete
    if (cleanValue.endsWith(' ') && index < 11) {
      newWords[index] = cleanValue.trim()
      setWords(newWords)
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Move to previous field on backspace if current field is empty
    if (e.key === 'Backspace' && words[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    // Move to next field on Enter or Space (if not empty)
    if ((e.key === 'Enter' || e.key === ' ') && words[index].trim() !== '') {
      e.preventDefault()
      if (index < 11) {
        inputRefs.current[index + 1]?.focus()
      }
    }

    // Arrow key navigation
    if (e.key === 'ArrowDown' && index < 9) {
      e.preventDefault()
      inputRefs.current[index + 3]?.focus()
    }
    if (e.key === 'ArrowUp' && index >= 3) {
      e.preventDefault()
      inputRefs.current[index - 3]?.focus()
    }
    if (e.key === 'ArrowRight' && index < 11) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const pastedWords = pastedText
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .slice(0, 12)

    const newWords = [...words]
    pastedWords.forEach((word, index) => {
      if (index < 12) {
        newWords[index] = word
      }
    })

    setWords(newWords)
    setPasted(true)
    setTimeout(() => setPasted(false), 2000)

    // Focus the first empty field or the last field
    const firstEmptyIndex = newWords.findIndex((w) => w === '')
    if (firstEmptyIndex !== -1) {
      inputRefs.current[firstEmptyIndex]?.focus()
    } else {
      inputRefs.current[11]?.focus()
    }
  }

  const handlePasteButton = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const pastedWords = text.trim().toLowerCase().split(/\s+/).slice(0, 12)

      const newWords = [...words]
      pastedWords.forEach((word, index) => {
        if (index < 12) {
          newWords[index] = word
        }
      })

      setWords(newWords)
      setPasted(true)
      setTimeout(() => setPasted(false), 2000)

      // Focus the first empty field or the last field
      const firstEmptyIndex = newWords.findIndex((w) => w === '')
      if (firstEmptyIndex !== -1) {
        inputRefs.current[firstEmptyIndex]?.focus()
      } else {
        inputRefs.current[11]?.focus()
      }
    } catch (error) {
      console.error('Failed to paste:', error)
    }
  }

  const getInputClassName = (index: number) => {
    const word = words[index].trim()

    // Base classes
    let classes =
      'relative flex items-center gap-2 rounded-lg p-3 transition-all'

    // Background based on focus or validation
    if (focusedIndex === index) {
      classes += ' bg-card-e2 ring-2 ring-primary'
    } else if (!showValidation || word === '' || !expectedMnemonic) {
      classes += ' bg-card-e2/80'
    } else if (validationState.incorrectIndices.includes(index)) {
      classes += ' bg-red-500/10 ring-1 ring-red-500/30'
    } else if (word.toLowerCase() === expectedWords[index]) {
      classes += ' bg-success/10 ring-1 ring-success/30'
    } else {
      classes += ' bg-card-e2/80'
    }

    return classes
  }

  const getInputIcon = (index: number) => {
    if (!showValidation || !expectedMnemonic) return null

    const word = words[index].trim()
    if (word === '') return null

    if (validationState.incorrectIndices.includes(index)) {
      return <X className="w-4 h-4 text-red-500" />
    }

    if (word.toLowerCase() === expectedWords[index]) {
      return <Check className="w-4 h-4 text-success" />
    }

    return null
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Header with paste button */}
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
        <p className="text-text-secondary font-medium text-center">
          {title || t('recover.input_title')}
        </p>
        <button
          onClick={handlePasteButton}
          className="flex items-center place-self-end shrink-0 w-min gap-2 p-3 rounded-xl bg-card hover:bg-card/80 transition-colors text-text-secondary text-sm"
        >
          {pasted ? (
            <>
              <Check className="w-4 h-4 text-success" />
              <span className="hidden sm:inline text-success">
                {t('recover.pasted')}
              </span>
            </>
          ) : (
            <>
              <Clipboard className="w-5 h-5" />
              <span>{t('recover.paste')}</span>
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="flex gap-3 p-4 bg-card rounded-xl border border-border-base">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-primary" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-main">
            {t('recover.enter_phrase')}
          </p>
          <p className="text-sm text-text-secondary leading-relaxed">
            {t('recover.enter_phrase_desc')}
          </p>
        </div>
      </div>

      {/* Input Grid */}
      <div className="grid grid-cols-2 gap-2 p-4 bg-card rounded-xl">
        {words.map((word, index) => (
          <div key={index} className={getInputClassName(index)}>
            {/* Word number */}
            <span className="text-xs text-text-secondary min-w-4">
              {index + 1}
            </span>

            {/* Input field */}
            <input
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              type="text"
              value={word}
              onChange={(e) => handleWordChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              placeholder={`Word ${index + 1}`}
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-text-main font-bold placeholder:text-text-secondary/30 placeholder:font-normal"
              autoComplete="off"
              spellCheck="false"
            />

            {/* Validation icon */}
            {getInputIcon(index) && (
              <div className="shrink-0">{getInputIcon(index)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Validation message */}
      {showValidation && words.every((w) => w.trim() !== '') && (
        <div
          className={`flex items-center gap-2 p-4 rounded-xl border ${
            validationState.isValid
              ? 'bg-success/10 border-success/30'
              : expectedMnemonic
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-success/10 border-success/30'
          }`}
        >
          {validationState.isValid ? (
            <>
              <Check className="w-5 h-5 text-success shrink-0" />
              <p className="text-sm font-medium text-success">
                {expectedMnemonic
                  ? 'Recovery phrase verified successfully!'
                  : 'Recovery phrase entered successfully!'}
              </p>
            </>
          ) : expectedMnemonic ? (
            <>
              <X className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-500">
                Some words don't match. Please check the highlighted fields.
              </p>
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default RecoveryPhraseInput
